import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tab, Tabs } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'hooks/useRouteMatch';
import { BrandSelect } from 'components/filter/input/BrandSelect';
import { BenchmarkSelect } from 'components/filter/input/BenchmarkSelect';
import { InfoButton } from 'components/InfoButton';
import { MarketplaceSelect } from 'components/filter/input/MarketplaceSelect';
import { CountrySelect } from 'components/filter/input/CountrySelect';
import { CategorySelect } from 'components/filter/input/CategorySelect';
import { BenchmarkingDateRangePicker } from 'components/filter/modules/benchmarking';
import { PageHeader } from 'components/Layout/PageContainer';

interface BenchmarkingTab {
  route: string;
  messageId: string;
  defaultMessage: string;
}

const MARKETPLACES = 'Marketplaces';
const MARKETPLACE_DRILLDOWN = 'Marketplace Drilldown';
const COUNTRIES = 'Countries';
const COUNTRY_DRILLDOWN = 'Country Drilldown';
const CATEGORIES = 'Categories';
const benchmarkingTabs: BenchmarkingTab[] = [
  {
    route: '/benchmarking/performance',
    messageId: 'benchmarking.performance',
    defaultMessage: 'Performance',
  },
  {
    route: '/benchmarking/marketplaces',
    messageId: 'benchmarking.marketplaces',
    defaultMessage: MARKETPLACES,
  },
  {
    route: '/benchmarking/countries',
    messageId: 'benchmarking.countries',
    defaultMessage: COUNTRIES,
  },
  {
    route: '/benchmarking/categories',
    messageId: 'benchmarking.categories',
    defaultMessage: CATEGORIES,
  },
];

export function Benchmarking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { matchRoute } = useRouteMatch(benchmarkingTabs);

  let currentSubPage;
  // required to maintain selected tab during drilldown
  if (location.pathname.startsWith('/benchmarking/drilldown/')) {
    if (location.pathname.includes('chnMarketplace')) {
      currentSubPage = { ...benchmarkingTabs[1], defaultMessage: MARKETPLACE_DRILLDOWN };
    } else if (location.pathname.includes('chnCountry')) {
      currentSubPage = { ...benchmarkingTabs[2], defaultMessage: COUNTRY_DRILLDOWN };
    } else {
      currentSubPage = benchmarkingTabs[0];
    }
  } else {
    currentSubPage = (matchRoute() ?? benchmarkingTabs[0]) as BenchmarkingTab;
  }

  const [searchParams] = useSearchParams();

  const handleChange = (_event: unknown, newSubPagePath: string | null) => {
    if (newSubPagePath !== null) {
      // Need to replace wildcard for sub routes to retain search params
      const cleanPath = newSubPagePath.replace('/*', '');
      const params = searchParams.toString();
      navigate(`${cleanPath}?${params}`);
    }
  };

  return (
    <div className=" benchmarking">
      <PageHeader
        topRightComponent={
          <div className="flex flex-row gap-2">
            {(currentSubPage.defaultMessage === MARKETPLACES ||
              currentSubPage.defaultMessage === COUNTRY_DRILLDOWN) && <MarketplaceSelect />}
            {(currentSubPage.defaultMessage === COUNTRIES ||
              currentSubPage.defaultMessage === MARKETPLACE_DRILLDOWN) && <CountrySelect />}
            {currentSubPage.defaultMessage === CATEGORIES && <CategorySelect />}
            <InfoButton showText />
          </div>
        }
        tabs={
          <Tabs value={currentSubPage.route} onChange={handleChange}>
            {benchmarkingTabs.map(tab => (
              <Tab
                key={tab.route}
                value={tab.route}
                id={tab.messageId}
                onClick={() => {
                  void navigate(`${tab.route}?${searchParams}`);
                }}
                label={<FormattedMessage id={tab.messageId} defaultMessage={tab.defaultMessage} />}
                className="hover:bg-denim-50"
              />
            ))}
          </Tabs>
        }
      >
        <BenchmarkingDateRangePicker.Main caption="filter.key.selectedDateRange" />
        <BenchmarkingDateRangePicker.Comparison caption="filter.key.selectedDateRangeComparison" />
        <BrandSelect />
        <BenchmarkSelect />
      </PageHeader>
      <div className="standard-page-container">
        <Outlet />
      </div>
    </div>
  );
}
